import strappedConnected from "modules/Core/higher-order-components/strappedConnected";
import cells from "modules/auth/state";
import LegalLayout, { Callout, ExternalLink } from "../../LegalLayout";

const CONTACT_EMAIL = "morrisgaby987@gmail.com";

const Email = () => <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;

const SECTIONS = [
    {
        id: "controller",
        title: "1. Who is responsible for your information?",
        content: (
            <p>
                Gabriel Morris is the data controller for the personal information processed through Transient Spaces,
                except where a third-party service acts as an independent controller under its own terms and privacy policy.
            </p>
        ),
    },
    {
        id: "eligibility",
        title: "2. Who may use Transient Spaces?",
        content: (
            <>
                <p>
                    Transient Spaces is intended only for people aged <strong>18 or over</strong>. It is not designed for
                    children, and we do not knowingly collect personal information from anyone under 18.
                </p>
                <p>
                    If you believe that someone under 18 has created an account or provided personal information, please
                    contact us so that we can investigate and delete the information where appropriate.
                </p>
            </>
        ),
    },
    {
        id: "information-we-collect",
        title: "3. Information we collect and process",
        content: (
            <>
                <h3>3.1 Account and authentication information</h3>
                <p>When you create or use an account, we process:</p>
                <ul>
                    <li>your email address;</li>
                    <li>a unique account identifier, including your Firebase user identifier;</li>
                    <li>an optional username, if you choose to provide one;</li>
                    <li>authentication status and information needed to keep you signed in; and</li>
                    <li>the authentication method you use, such as email and password or Google Sign-In.</li>
                </ul>
                <p>Passwords are handled by Firebase Authentication. We do not receive or store your readable password.</p>
                <p>
                    If you use Google Sign-In, Google provides the information required to authenticate your account.
                    Transient Spaces retains your email address and authentication identifier but does <strong>not</strong> save
                    your Google profile name or profile photograph.
                </p>

                <h3>3.2 Content you create</h3>
                <p>We process the information you choose to create and store in the application, including:</p>
                <ul>
                    <li>transient spaces or “Booths”;</li>
                    <li>roadmaps, checkpoints and sub-checkpoints;</li>
                    <li>journal entries and reflections;</li>
                    <li>tasks and task status;</li>
                    <li>time-tracking records;</li>
                    <li>dates, times and other information associated with that content; and</li>
                    <li>other text you voluntarily enter into the application.</li>
                </ul>
                <p>
                    This content is private to your account unless the application clearly tells you otherwise. Transient
                    Spaces does not currently provide public posting or social-sharing features.
                </p>

                <h3>3.3 Sensitive information you choose to enter</h3>
                <p>
                    Transient Spaces does not require you to provide health information, religious or philosophical beliefs,
                    or other legally protected sensitive information. However, because the application includes free-text
                    journalling and planning features, you may choose to include information of this kind.
                </p>
                <p>
                    If you deliberately include sensitive information in content you save, it is processed only to store and
                    display that content at your direction. We do not use journal content to diagnose you, infer sensitive
                    characteristics, advertise to you, or build a marketing profile. Where applicable law requires an
                    additional condition for processing this information, we rely on your explicit choice and consent to
                    store the content. You can withdraw that consent by deleting the relevant content or your account.
                </p>
                <Callout tone="warning">
                    Transient Spaces is not a medical or mental-health service and should not be used to obtain medical
                    advice or emergency assistance.
                </Callout>

                <h3>3.4 Technical information processed by service providers</h3>
                <p>
                    The Transient Spaces backend is not configured to maintain request-access logs or store users’ IP
                    addresses or device information.
                </p>
                <p>
                    Some limited technical information is nevertheless processed by service providers when you use
                    authentication or receive application updates:
                </p>
                <ul>
                    <li>
                        <strong>Firebase Authentication</strong> may process IP addresses, user-agent information,
                        authentication events and security information to authenticate users, prevent abuse and protect
                        accounts.
                    </li>
                    <li>
                        <strong>Google Sign-In</strong> processes information needed to complete sign-in and protect the
                        Google account authentication process.
                    </li>
                    <li>
                        <strong>Expo Application Services</strong>, including EAS Update, receives requests from the
                        application to check for and deliver compatible software updates. Those requests may include
                        technical information such as the application version, platform and network information ordinarily
                        associated with an internet request.
                    </li>
                </ul>
                <p>
                    We do not use analytics or advertising SDKs, and we do not operate our own system for tracking users
                    across apps or websites.
                </p>

                <h3>3.5 Information stored on your device</h3>
                <p>
                    Transient Spaces uses local storage, including AsyncStorage, to retain limited application state,
                    authentication information and settings on your device. Local information may remain until you sign
                    out, clear the application’s data or uninstall the application.
                </p>
            </>
        ),
    },
    {
        id: "not-collected",
        title: "4. Information we do not currently collect",
        content: (
            <>
                <p>During the pilot programme, Transient Spaces does not:</p>
                <ul>
                    <li>collect payment-card or billing information;</li>
                    <li>process payments or subscriptions through Stripe;</li>
                    <li>serve advertising;</li>
                    <li>sell personal information;</li>
                    <li>use personal information for targeted advertising;</li>
                    <li>use analytics or crash-reporting services;</li>
                    <li>send push notifications;</li>
                    <li>request access to your precise location, contacts, camera, microphone or photo library;</li>
                    <li>save your Google profile name or photograph; or</li>
                    <li>make automated decisions that produce legal or similarly significant effects.</li>
                </ul>
                <p>If these practices change, this Privacy Policy will be updated before the new processing begins where required.</p>
            </>
        ),
    },
    {
        id: "how-we-use",
        title: "5. How and why we use your information",
        content: (
            <>
                <p>We use personal information to:</p>
                <ul>
                    <li>create, authenticate and maintain your account;</li>
                    <li>provide the application and its journalling, planning, roadmap, task and time-tracking features;</li>
                    <li>save, retrieve and synchronise your content;</li>
                    <li>maintain account and application security;</li>
                    <li>deliver compatible application updates;</li>
                    <li>respond to support, privacy and account requests;</li>
                    <li>investigate technical or security problems; and</li>
                    <li>meet legal obligations and protect legal rights where necessary.</li>
                </ul>
            </>
        ),
    },
    {
        id: "lawful-bases",
        title: "6. Our lawful bases",
        content: (
            <>
                <p>Under UK data-protection law, we rely on the following lawful bases:</p>
                <ul>
                    <li>
                        <strong>Contract:</strong> processing account information and user content is necessary to provide
                        the service you request when you create and use an account.
                    </li>
                    <li>
                        <strong>Legitimate interests:</strong> limited processing is necessary to secure, maintain and
                        improve the reliability of the service, prevent misuse, deliver updates and respond to support
                        requests. We consider these interests against your rights and expectations.
                    </li>
                    <li>
                        <strong>Legal obligation:</strong> information may be processed where necessary to comply with
                        applicable law or a valid legal requirement.
                    </li>
                    <li>
                        <strong>Consent:</strong> where consent is required, including for sensitive information you
                        deliberately choose to place in free-text content, you may withdraw consent at any time without
                        affecting processing that occurred before withdrawal.
                    </li>
                </ul>
            </>
        ),
    },
    {
        id: "service-providers",
        title: "7. Service providers and disclosure",
        content: (
            <>
                <p>We use the following providers to operate Transient Spaces:</p>

                <h3>Google Firebase and Google Sign-In</h3>
                <p>
                    Google provides account authentication and Google Sign-In. Google processes authentication and security
                    information in accordance with its applicable terms and privacy documentation.
                </p>
                <ul>
                    <li>Firebase privacy and security information: <ExternalLink href="https://firebase.google.com/support/privacy" /></li>
                    <li>Google Privacy Policy: <ExternalLink href="https://policies.google.com/privacy" /></li>
                </ul>

                <h3>DigitalOcean</h3>
                <p>
                    DigitalOcean hosts the backend service and managed MongoDB database. The managed database is located in
                    DigitalOcean’s <strong>London (<code>LON1</code>) region</strong>.
                </p>
                <ul>
                    <li>DigitalOcean Privacy Policy: <ExternalLink href="https://www.digitalocean.com/legal/privacy-policy" /></li>
                </ul>

                <h3>Expo Application Services</h3>
                <p>
                    Expo provides application build and update-delivery services. The installed application may contact
                    Expo’s update service to check for and download compatible updates.
                </p>
                <ul>
                    <li>Expo Privacy Policy: <ExternalLink href="https://expo.dev/privacy" /></li>
                    <li>
                        Expo data and privacy information:{" "}
                        <ExternalLink href="https://docs.expo.dev/regulatory-compliance/data-and-privacy-protection/" />
                    </li>
                </ul>

                <p>
                    These providers process information only for the relevant services they provide, subject to their own
                    terms and legal obligations.
                </p>
                <p>We may also disclose information where reasonably necessary to:</p>
                <ul>
                    <li>comply with a legal obligation, court order or lawful request;</li>
                    <li>establish, exercise or defend legal rights;</li>
                    <li>protect users, the service or others from fraud, abuse or security threats; or</li>
                    <li>support a reorganisation or transfer of the service, provided appropriate protections apply.</li>
                </ul>
                <Callout title="We do not sell or rent personal information." />
            </>
        ),
    },
    {
        id: "international",
        title: "8. International processing",
        content: (
            <>
                <p>
                    The primary application database is hosted in the United Kingdom. Google, Firebase and Expo may process
                    limited account or technical information in other countries.
                </p>
                <p>
                    Where personal information is transferred outside the United Kingdom, we rely on applicable legal
                    safeguards made available by the relevant provider, which may include UK adequacy regulations and
                    approved contractual protections.
                </p>
            </>
        ),
    },
    {
        id: "retention",
        title: "9. How long we retain information",
        content: (
            <>
                <p>
                    We retain active account information and user-created content for as long as your account remains open
                    or as needed to provide the service.
                </p>
                <p>
                    When you delete individual content, it is removed from the active application database. When account
                    deletion is completed, the associated active account information and application content are deleted,
                    unless limited retention is legally required. See <a href="/delete-account">how to delete your account</a>.
                </p>
                <p>
                    DigitalOcean’s managed MongoDB service creates automatic backups. Information deleted from the active
                    database may remain in those backups for up to <strong>seven days</strong>, after which it should expire
                    through the provider’s normal backup lifecycle. Backups are maintained for recovery and are not used as
                    an active source of user information.
                </p>
                <p>
                    Firebase may retain certain authentication security records, including logged IP addresses, for the
                    periods described in Firebase’s privacy documentation. Those provider-controlled records may remain for a
                    limited period after active application data is deleted.
                </p>
                <p>
                    Information stored locally on your device may remain until you sign out, clear the application’s storage
                    or uninstall the application.
                </p>
                <p>
                    We may retain minimal records for longer where necessary to comply with law, resolve disputes, prevent
                    fraud or demonstrate that a request was completed.
                </p>
            </>
        ),
    },
    {
        id: "security",
        title: "10. Security",
        content: (
            <>
                <p>
                    We use reasonable technical and organisational safeguards appropriate to the nature of the information
                    processed. These include encrypted network connections, authenticated access, access controls and
                    managed hosting services.
                </p>
                <p>
                    No system can be guaranteed completely secure. You are responsible for keeping your account credentials
                    secure and for choosing carefully what information you place in journal entries and other free-text
                    fields.
                </p>
            </>
        ),
    },
    {
        id: "your-rights",
        title: "11. Your rights",
        content: (
            <>
                <p>Depending on the circumstances, UK data-protection law may give you the right to:</p>
                <ul>
                    <li>request access to your personal information;</li>
                    <li>ask us to correct inaccurate or incomplete information;</li>
                    <li>request deletion of your personal information;</li>
                    <li>ask us to restrict how information is used;</li>
                    <li>object to processing based on legitimate interests;</li>
                    <li>receive certain information in a portable format;</li>
                    <li>withdraw consent where processing relies on consent; and</li>
                    <li>complain to a data-protection regulator.</li>
                </ul>
                <p>
                    To exercise a right, email <Email />. We may need to verify that the request relates to your account
                    before acting on it.
                </p>
                <p>You may also complain to the UK Information Commissioner’s Office:</p>
                <ul>
                    <li>Website: <ExternalLink href="https://ico.org.uk/make-a-complaint/" /></li>
                    <li>Telephone: <a href="tel:03031231113">0303 123 1113</a></li>
                </ul>
            </>
        ),
    },
    {
        id: "changes",
        title: "12. Changes to this policy",
        content: (
            <>
                <p>
                    We may update this Privacy Policy when the application, its providers or legal requirements change. The
                    updated policy will show a revised “Last updated” date. If a change materially affects how personal
                    information is used, we will provide additional notice where reasonably appropriate or legally required.
                </p>
                <p>
                    The policy must be reviewed before introducing features such as subscriptions, Stripe payments,
                    advertising, analytics, crash reporting, push notifications, social sharing, image uploads or support
                    for users under 18.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        title: "13. Contact",
        highlight: true,
        content: (
            <>
                <p>
                    For questions, concerns or requests about this Privacy Policy or the use of your personal information,
                    contact:
                </p>
                <p>
                    <strong>Gabriel Morris</strong>
                    <br />
                    Developer of Transient Spaces
                    <br />
                    England, United Kingdom
                    <br />
                    Email: <Email />
                </p>
            </>
        ),
    },
];

const PrivacyPolicyPage = ({ isAuthed }) => (
    <LegalLayout
        isAuthed={isAuthed}
        eyebrow="Legal"
        title="Privacy Policy"
        meta={[
            { label: "Effective", value: "23 September 2026" },
            { label: "Last updated", value: "23 September 2026" },
        ]}
        intro={
            <>
                <p>
                    Transient Spaces is a personal planning, journalling and progress-tracking application operated by{" "}
                    <strong>Gabriel Morris</strong>, an individual developer based in England, United Kingdom.
                </p>
                <p>
                    This Privacy Policy explains what personal information Transient Spaces collects and processes, why it
                    is used, where it is stored, who processes it on our behalf, how long it is retained, and the choices and
                    rights available to you.
                </p>
            </>
        }
        aside={
            <>
                Privacy questions or requests?
                <br />
                <Email />
            </>
        }
        sections={SECTIONS}
    />
);

export default strappedConnected(
    PrivacyPolicyPage,
    { isAuthed: cells.validateToken.selectors.isAuthed },
    {},
    () => ({})
);
